import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
// Payment functionality removed from platform admin - only store admins can process payments
import { getLogoUrl } from "utils/logoUtils";
import { COUNTRIES } from "utils/countryCurrencyData";

const PlatformStoreDetail: React.FC = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const { isAuthenticated } = usePlatformAuth();
  const navigate = useNavigate();
  const [store, setStore] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [enabledPlugins, setEnabledPlugins] = useState<any[]>([]);
  const [nginxStatus, setNginxStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<any[]>([]);
  const [plugins, setPlugins] = useState<any[]>([]);
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [selectedPlugins, setSelectedPlugins] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Payment functionality removed - only store admins can process payments
  const [activating, setActivating] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [applyImmediate, setApplyImmediate] = useState(false);
  const [processRefund, setProcessRefund] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState<
    string | null
  >(null);
  const [countryOverwrite, setCountryOverwrite] = useState<string>("");
  const [updatingCountryOverwrite, setUpdatingCountryOverwrite] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/platform/login", { replace: true });
      return;
    }

    if (storeId) {
      fetchStoreDetails();
      fetchPlans();
      fetchPlugins();
    }
  }, [isAuthenticated, navigate, storeId]);

  // Ref to track polling interval and current status to avoid flickering
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const nginxStatusRef = useRef(nginxStatus);

  // Update ref when nginxStatus changes (without causing re-renders)
  useEffect(() => {
    nginxStatusRef.current = nginxStatus;
  }, [nginxStatus]);

  // Poll nginx status if any operation is in progress
  useEffect(() => {
    if (!storeId || !store?.subdomain) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Check current status from ref to avoid dependency on nginxStatus
    const currentStatus = nginxStatusRef.current;
    const hasInProgress =
      currentStatus?.status?.dns?.status === "in_progress" ||
      currentStatus?.status?.ssl?.status === "in_progress" ||
      currentStatus?.status?.nginx?.status === "in_progress";

    // Start polling if in progress and not already polling
    if (hasInProgress && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        platformApiService
          .getStoreNginxStatus(storeId)
          .then((response) => {
            const newStatus = response.data;
            const currentStatus = nginxStatusRef.current;

            // Only update if status actually changed to avoid unnecessary re-renders
            const statusChanged =
              JSON.stringify(newStatus?.status) !==
                JSON.stringify(currentStatus?.status) ||
              JSON.stringify(newStatus?.nginx) !==
                JSON.stringify(currentStatus?.nginx);

            if (statusChanged) {
              setNginxStatus(newStatus);
            }

            // Stop polling if no operations are in progress anymore
            const stillInProgress =
              newStatus?.status?.dns?.status === "in_progress" ||
              newStatus?.status?.ssl?.status === "in_progress" ||
              newStatus?.status?.nginx?.status === "in_progress";

            if (!stillInProgress && pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          })
          .catch((error) => {
            console.error("Error polling nginx status:", error);
          });
      }, 5000);
    } else if (!hasInProgress && pollingRef.current) {
      // Stop polling if no operations are in progress
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [
    storeId,
    store?.subdomain,
    nginxStatus?.status?.dns?.status,
    nginxStatus?.status?.ssl?.status,
    nginxStatus?.status?.nginx?.status,
  ]);

  // Initialize selected plugins and plan when manage subscription modal opens
  useEffect(() => {
    if (showManageSubscription && subscription) {
      // Initialize selected plan from subscription
      if (!selectedPlan && subscription.plan) {
        const planId = (subscription.plan as any)?._id || subscription.planId;
        if (planId) {
          setSelectedPlan(planId);
        }
      }

      // Initialize billing cycle from subscription
      if (subscription.billingCycle) {
        setBillingCycle(subscription.billingCycle as "monthly" | "yearly");
      }

      // Initialize selected plugins from subscription's pluginIds (single source of truth)
      const subscriptionPluginIds = subscription.pluginIds || [];
      // Only set if different to avoid unnecessary updates
      if (
        JSON.stringify(subscriptionPluginIds.sort()) !==
        JSON.stringify(selectedPlugins.sort())
      ) {
        setSelectedPlugins(subscriptionPluginIds);
      }
    } else if (!showManageSubscription) {
      // Clear selection when modal closes
      setSelectedPlugins([]);
      setSelectedPlan("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManageSubscription, subscription]);

  const fetchStoreDetails = async () => {
    try {
      const response = await platformApiService.getStore(storeId!);
      setStore(response.data.store);
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
      setEnabledPlugins(response.data.enabledPlugins || []);
      setNginxStatus(response.data.nginxStatus || null);
      setCountryOverwrite(response.data.store?.countryOverwrite || "");

      // Fetch nginx status separately if not included
      if (response.data.store?.subdomain && !response.data.nginxStatus) {
        try {
          const statusResponse = await platformApiService.getStoreNginxStatus(
            storeId!
          );
          setNginxStatus(statusResponse.data);
        } catch (statusError) {
          console.error("Error fetching nginx status:", statusError);
        }
      }
    } catch (error) {
      console.error("Error fetching store details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await platformApiService.getPlans();
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchPlugins = async () => {
    try {
      const response = await platformApiService.getPlugins();
      setPlugins(response.data.plugins || []);
    } catch (error) {
      console.error("Error fetching plugins:", error);
    }
  };

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) {
      setError("Please select a plan");
      return;
    }

    // For plugin-based stores, require at least one plugin
    if (
      store?.businessModel === "plugin-based" &&
      selectedPlugins.length === 0
    ) {
      setError("Please select at least one plugin/feature");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const subscriptionData: any = {
        planId: selectedPlan,
        billingCycle,
      };

      // Add plugin IDs for plugin-based stores
      if (
        store?.businessModel === "plugin-based" &&
        selectedPlugins.length > 0
      ) {
        subscriptionData.pluginIds = selectedPlugins;
      }

      const response = await platformApiService.manageStoreSubscription(
        storeId!,
        "create",
        subscriptionData
      );

      // Platform admin creates subscription - store admin will need to complete payment
      setShowCreateSubscription(false);
      setSelectedPlan("");
      setSelectedPlugins([]);
      fetchStoreDetails(); // Refresh store details

      if (response.data?.requiresPayment) {
        alert(
          "Subscription created. Store admin needs to complete payment in their portal to activate it."
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create subscription");
    } finally {
      setSubmitting(false);
    }
  };

  // Payment functionality removed - only store admins can process payments

  const handleManualActivation = async () => {
    if (
      !window.confirm(
        "Are you sure you want to manually activate this subscription?"
      )
    ) {
      return;
    }

    try {
      setActivating(true);
      setError(null);
      await platformApiService.activateSubscription(storeId!);
      await fetchStoreDetails(); // Refresh store details
      alert("Subscription activated successfully!");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to activate subscription");
    } finally {
      setActivating(false);
    }
  };

  const handleManualExpiration = async () => {
    if (
      !window.confirm(
        "⚠️ WARNING: This will immediately expire the subscription.\n\n" +
        "This action is for testing purposes only and is not available in production.\n\n" +
        "Are you sure you want to expire this subscription?"
      )
    ) {
      return;
    }

    try {
      setExpiring(true);
      setError(null);
      await platformApiService.expireSubscription(storeId!);
      await fetchStoreDetails(); // Refresh store details
      alert("Subscription expired successfully (testing only)");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to expire subscription");
    } finally {
      setExpiring(false);
    }
  };

  const handlePluginToggle = (pluginId: string) => {
    setSelectedPlugins((prev) =>
      prev.includes(pluginId)
        ? prev.filter((id) => id !== pluginId)
        : [...prev, pluginId]
    );
  };

  const handleDeleteStore = async () => {
    if (!store) return;

    const confirmMessage =
      `Are you sure you want to PERMANENTLY delete the store "${store.name}"?\n\n` +
      `This will:\n` +
      `- Delete the store and all its data\n` +
      `- Delete all products, orders, users, and subscriptions\n` +
      `- Clean up subdomain configuration (DNS + SSL + Nginx)\n\n` +
      `This action CANNOT be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation
    if (
      !window.confirm(
        `Final confirmation: Delete "${store.name}"? This is your last chance to cancel.`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      await platformApiService.deleteStore(storeId!);
      alert(`Store "${store.name}" has been deleted successfully.`);
      navigate("/platform/stores");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete store");
      alert(
        `Failed to delete store: ${err.response?.data?.error || "Unknown error"}`
      );
    } finally {
      setDeleting(false);
    }
  };

  // Calculate subscription total
  const calculateTotal = () => {
    let total = 0;

    // Add plan price
    if (selectedPlan) {
      const plan = plans.find((p) => p._id === selectedPlan);
      if (plan) {
        if (billingCycle === "yearly" && plan.yearlyPrice) {
          total += plan.yearlyPrice;
        } else {
          total += plan.price * (billingCycle === "yearly" ? 12 : 1);
        }
      }
    }

    // Add selected plugins' prices
    if (store?.businessModel === "plugin-based" && selectedPlugins.length > 0) {
      selectedPlugins.forEach((pluginId) => {
        const plugin = plugins.find((p) => p.id === pluginId);
        if (plugin) {
          if (billingCycle === "yearly" && plugin.yearlyPrice) {
            total += plugin.yearlyPrice;
          } else {
            total += plugin.price * (billingCycle === "yearly" ? 12 : 1);
          }
        }
      });
    }

    return total;
  };

  const handleUpdateSubscription = async (newPlanId: string) => {
    setSubmitting(true);
    setError(null);

    try {
      const updateData: any = {
        planId: newPlanId,
        immediate: applyImmediate, // Platform admin can apply immediately
      };

      // Add plugin IDs for plugin-based stores
      if (
        store?.businessModel === "plugin-based" &&
        selectedPlugins.length > 0
      ) {
        updateData.pluginIds = selectedPlugins;
      }

      const response = await platformApiService.manageStoreSubscription(
        storeId!,
        "update",
        updateData
      );

      // If payment is required (for manual renewal reactivation), inform user
      if (response.data?.requiresPayment && response.data?.clientSecret) {
        alert(
          "Subscription updated. Payment is required to activate. Store admin needs to complete payment in their portal."
        );
      } else if (response.data.message) {
        alert(response.data.message);
      }

      setShowManageSubscription(false);
      setSelectedPlugins([]);
      setApplyImmediate(false);
      // Refresh store details to get updated subscription
      await fetchStoreDetails();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    const cancelMessage = applyImmediate
      ? "Are you sure you want to cancel this subscription immediately?" +
        (processRefund ? "\n\nA time-based refund will be processed." : "")
      : "Are you sure you want to cancel this subscription at the end of the current period?";

    if (!window.confirm(cancelMessage)) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const cancelData: any = {
        immediate: applyImmediate, // Platform admin can cancel immediately
        refund: processRefund, // Process refund if requested
      };

      const response = await platformApiService.manageStoreSubscription(
        storeId!,
        "cancel",
        cancelData
      );

      // Show success message with refund info if applicable
      let message =
        response.data.message || "Subscription cancelled successfully";
      if (response.data.refundAmount) {
        message += `\n\nRefund processed: $${response.data.refundAmount.toFixed(2)}`;
      }
      alert(message);

      setShowManageSubscription(false);
      setApplyImmediate(false);
      setProcessRefund(false);
      fetchStoreDetails();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to cancel subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendPasswordReset = async () => {
    const storeAdminEmail = store?.storeAdminEmail;

    if (!storeAdminEmail) {
      alert(
        "Store admin email not found. Please ensure the store has an admin user."
      );
      return;
    }

    if (!window.confirm(`Send password reset email to ${storeAdminEmail}?`)) {
      return;
    }

    setSendingPasswordReset(true);
    setPasswordResetSuccess(null);
    setError(null);

    try {
      await platformApiService.sendStoreAdminPasswordReset(
        storeId!,
        storeAdminEmail
      );
      setPasswordResetSuccess(
        `Password reset email sent to ${storeAdminEmail}`
      );
      setTimeout(() => {
        setPasswordResetSuccess(null);
      }, 5000);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to send password reset email"
      );
      setPasswordResetSuccess(null);
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleUpdateCountryOverwrite = async () => {
    setUpdatingCountryOverwrite(true);
    setError(null);

    try {
      // Send empty string as null to clear the override, or the country code if set
      const overrideValue = countryOverwrite && countryOverwrite.trim() !== "" 
        ? countryOverwrite.trim().toUpperCase() 
        : null;
      
      await platformApiService.updateStore(storeId!, {
        countryOverwrite: overrideValue,
      });
      // Refresh store details to get updated data
      await fetchStoreDetails();
      setPasswordResetSuccess("Country overwrite updated successfully");
      setTimeout(() => {
        setPasswordResetSuccess(null);
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to update country overwrite"
      );
    } finally {
      setUpdatingCountryOverwrite(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading store details...</div>;
  }

  if (!store) {
    return <div className="p-8">{t("notFound.storeNotFound")}</div>;
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate("/platform/stores")}
            className="text-indigo-600 hover:text-indigo-900 mb-4"
          >
            ← Back to Stores
          </button>
          <div className="flex items-center gap-4">
            {store.logo && (
              <img
                src={getLogoUrl(store.logo)}
                alt={`${store.name} logo`}
                className="h-12 w-12 object-contain rounded border border-gray-200 bg-white p-1"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <h1 className="text-3xl font-bold">{store.name}</h1>
          </div>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/platform/stores/${storeId}/products`}
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700"
          >
            Manage Products
          </Link>
          <button
            onClick={() => navigate(`/platform/stores/${storeId}/edit`)}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
          >
            Edit Store
          </button>
          <button
            onClick={handleDeleteStore}
            disabled={deleting}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? "Deleting..." : "Delete Store"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Store Information</h2>

          {/* Basic Info Grid */}
          <dl className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Domain
              </dt>
              <dd className="text-sm text-gray-900 mt-1 break-all">
                {store.domain || "N/A"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Subdomain
              </dt>
              <dd className="text-sm text-gray-900 mt-1">
                {store.subdomain || "N/A"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Business Model
              </dt>
              <dd className="text-sm text-gray-900 mt-1 capitalize">
                {store.businessModel?.replace("-", " ") || "N/A"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Business Type
              </dt>
              <dd className="text-sm text-gray-900 mt-1">
                {store.businessType || "N/A"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Status
              </dt>
              <dd className="text-sm mt-1">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold ${
                    store.subscriptionStatus === "active"
                      ? "bg-green-100 text-green-800"
                      : store.subscriptionStatus === "trialing"
                        ? "bg-blue-100 text-blue-800"
                        : store.subscriptionStatus === "incomplete" ||
                            store.subscriptionStatus === "incomplete_expired"
                          ? "bg-yellow-100 text-yellow-800"
                          : store.subscriptionStatus === "past_due"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-red-100 text-red-800"
                  }`}
                >
                  {store.subscriptionStatus}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">
                Active
              </dt>
              <dd className="text-sm mt-1">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold ${
                    store.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {store.isActive ? "Yes" : "No"}
                </span>
              </dd>
            </div>
            {store.businessModel === "pay-as-you-go" &&
              store.transactionFeePercentage !== undefined && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">
                    Transaction Fee
                  </dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {store.transactionFeePercentage}%
                  </dd>
                </div>
              )}
            {store.createdAt && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">
                  Created
                </dt>
                <dd className="text-sm text-gray-900 mt-1">
                  {new Date(store.createdAt).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>

          {/* Nginx/SSL Status */}
          {store.subdomain && nginxStatus && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Infrastructure Status
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Overall Status</span>
                  <span
                    className={`px-2 py-1 text-xs font-semibold ${
                      nginxStatus.status?.overall === "healthy"
                        ? "bg-green-100 text-green-800"
                        : nginxStatus.status?.overall === "degraded"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {nginxStatus.status?.overall || "Unknown"}
                  </span>
                </div>
                {nginxStatus.status?.dns && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">DNS</span>
                    <span
                      className={`px-2 py-1 text-xs ${
                        nginxStatus.status.dns.status === "success"
                          ? "bg-green-100 text-green-800"
                          : nginxStatus.status.dns.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {nginxStatus.status.dns.status}
                    </span>
                  </div>
                )}
                {nginxStatus.status?.ssl && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">
                      SSL Certificate
                    </span>
                    <span
                      className={`px-2 py-1 text-xs ${
                        nginxStatus.status.ssl.status === "success"
                          ? "bg-green-100 text-green-800"
                          : nginxStatus.status.ssl.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {nginxStatus.status.ssl.status}
                    </span>
                  </div>
                )}
                {nginxStatus.status?.nginx && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Nginx Config</span>
                    <span
                      className={`px-2 py-1 text-xs ${
                        nginxStatus.status.nginx.status === "success"
                          ? "bg-green-100 text-green-800"
                          : nginxStatus.status.nginx.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {nginxStatus.status.nginx.status}
                    </span>
                  </div>
                )}
                {nginxStatus.nginx?.sslValid &&
                  nginxStatus.nginx.daysUntilExpiry !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">SSL Expires</span>
                      <span
                        className={`text-xs ${
                          nginxStatus.nginx.daysUntilExpiry < 30
                            ? "text-red-600 font-semibold"
                            : nginxStatus.nginx.daysUntilExpiry < 60
                              ? "text-yellow-600"
                              : "text-gray-600"
                        }`}
                      >
                        {nginxStatus.nginx.daysUntilExpiry} days
                      </span>
                    </div>
                  )}
                {nginxStatus.status?.dns?.error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs text-red-800">
                      DNS Error: {nginxStatus.status.dns.error}
                    </p>
                  </div>
                )}
                {nginxStatus.status?.ssl?.error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs text-red-800">
                      SSL Error: {nginxStatus.status.ssl.error}
                    </p>
                  </div>
                )}
                {nginxStatus.status?.nginx?.error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs text-red-800">
                      Nginx Error: {nginxStatus.status.nginx.error}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Domains */}
          {store.customDomains && store.customDomains.length > 0 && (
            <div className="mb-4 pb-4 border-b">
              <dt className="text-xs font-medium text-gray-500 uppercase mb-2">
                Custom Domains
              </dt>
              <dd className="flex flex-wrap gap-2">
                {store.customDomains.map((domain: string, index: number) => (
                  <span
                    key={index}
                    className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                  >
                    {domain}
                  </span>
                ))}
              </dd>
            </div>
          )}

          {/* Country Override Settings - Only available in non-production environments */}
          {process.env.NODE_ENV !== "production" && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Location Testing Settings
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                    Country Override
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Override the detected country for all visitors to this store. This simulates users visiting from the selected country, affecting currency, tax rates, and location-based features.
                    <span className="block mt-1 font-semibold text-orange-600">
                      Note: This feature is only available in non-production environments.
                    </span>
                  </p>
                  <div className="flex items-center gap-3">
                    <select
                      value={countryOverwrite}
                      onChange={(e) => setCountryOverwrite(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Override (Use Detected Location)</option>
                      {COUNTRIES.sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleUpdateCountryOverwrite}
                      disabled={updatingCountryOverwrite}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {updatingCountryOverwrite ? "Saving..." : "Save"}
                    </button>
                  </div>
                  {countryOverwrite && (
                    <p className="mt-2 text-xs text-gray-600">
                      All visitors will be treated as if they are visiting from:{" "}
                      <span className="font-semibold">
                        {COUNTRIES.find((c) => c.code === countryOverwrite)?.name || countryOverwrite}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Store Admin Password Reset */}
          {store.storeAdminEmail && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Store Admin Password Reset
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Store Admin Email
                    </div>
                    <div className="text-sm text-gray-900">
                      {store.storeAdminEmail}
                    </div>
                  </div>
                  <button
                    onClick={handleSendPasswordReset}
                    disabled={sendingPasswordReset}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {sendingPasswordReset ? "Sending..." : "Send Reset Email"}
                  </button>
                </div>
                {passwordResetSuccess && (
                  <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                    {passwordResetSuccess}
                  </div>
                )}
                {error && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                    {error}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Send a password reset link to the store admin email address.
                </p>
              </div>
            </div>
          )}

          {/* Theme Colors */}
          {store.themePalette && (
            <div className="mb-4 pb-4 border-b">
              <dt className="text-xs font-medium text-gray-500 uppercase mb-2">
                Theme Colors
              </dt>
              <dd className="flex flex-wrap gap-2">
                {store.themePalette.primary && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: store.themePalette.primary }}
                      title="Primary"
                    />
                    <span className="text-xs text-gray-600">Primary</span>
                  </div>
                )}
                {store.themePalette.secondary && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: store.themePalette.secondary }}
                      title="Secondary"
                    />
                    <span className="text-xs text-gray-600">Secondary</span>
                  </div>
                )}
                {store.themePalette.accent && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: store.themePalette.accent }}
                      title="Accent"
                    />
                    <span className="text-xs text-gray-600">Accent</span>
                  </div>
                )}
                {store.themePalette.background && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: store.themePalette.background }}
                      title="Background"
                    />
                    <span className="text-xs text-gray-600">BG</span>
                  </div>
                )}
                {store.themePalette.text && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: store.themePalette.text }}
                      title="Text"
                    />
                    <span className="text-xs text-gray-600">Text</span>
                  </div>
                )}
              </dd>
            </div>
          )}

          {/* Font Family */}
          {store.fontFamily && (
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                Font Family
              </dt>
              <dd
                className="text-sm text-gray-900"
                style={{
                  fontFamily: "var(--store-font-family, 'Inter', sans-serif)",
                }}
              >
                {store.fontFamily}
              </dd>
            </div>
          )}

          {/* Payout Preferences - Only for stores with online payment enabled */}
          {store?.payoutPreferences &&
            (store.businessModel === "pay-as-you-go" ||
              enabledPlugins.some((ep: any) => ep.id === "online-payment")) && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Payout Preferences
                </h4>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">
                      Preferred Method
                    </dt>
                    <dd className="text-gray-900 mt-1 capitalize">
                      {store.payoutPreferences.preferredPayoutMethod
                        ? store.payoutPreferences.preferredPayoutMethod.replace(
                            "_",
                            " "
                          )
                        : "Not set"}
                    </dd>
                  </div>
                  {store.payoutPreferences.payoutDetails && (
                    <div className="mt-2">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Payout Details
                      </dt>
                      <dd className="text-gray-900 space-y-1">
                        {store.payoutPreferences.preferredPayoutMethod ===
                          "bank_transfer" && (
                          <>
                            {store.payoutPreferences.payoutDetails.bankName && (
                              <div className="text-xs">
                                <span className="font-medium">Bank:</span>{" "}
                                {store.payoutPreferences.payoutDetails.bankName}
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails
                              .accountHolderName && (
                              <div className="text-xs">
                                <span className="font-medium">
                                  Account Holder:
                                </span>{" "}
                                {
                                  store.payoutPreferences.payoutDetails
                                    .accountHolderName
                                }
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails
                              .accountNumber && (
                              <div className="text-xs">
                                <span className="font-medium">Account #:</span>{" "}
                                {
                                  store.payoutPreferences.payoutDetails
                                    .accountNumber
                                }
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails
                              .routingNumber && (
                              <div className="text-xs">
                                <span className="font-medium">Routing #:</span>{" "}
                                {
                                  store.payoutPreferences.payoutDetails
                                    .routingNumber
                                }
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails.iban && (
                              <div className="text-xs">
                                <span className="font-medium">IBAN:</span>{" "}
                                {store.payoutPreferences.payoutDetails.iban}
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails
                              .swiftCode && (
                              <div className="text-xs">
                                <span className="font-medium">SWIFT:</span>{" "}
                                {
                                  store.payoutPreferences.payoutDetails
                                    .swiftCode
                                }
                              </div>
                            )}
                            {store.payoutPreferences.payoutDetails
                              .bankCountry && (
                              <div className="text-xs">
                                <span className="font-medium">Country:</span>{" "}
                                {
                                  store.payoutPreferences.payoutDetails
                                    .bankCountry
                                }
                              </div>
                            )}
                          </>
                        )}
                        {store.payoutPreferences.preferredPayoutMethod ===
                          "paypal" &&
                          store.payoutPreferences.payoutDetails.paypalEmail && (
                            <div className="text-xs">
                              <span className="font-medium">PayPal Email:</span>{" "}
                              {
                                store.payoutPreferences.payoutDetails
                                  .paypalEmail
                              }
                            </div>
                          )}
                        {store.payoutPreferences.preferredPayoutMethod ===
                          "stripe_connect" &&
                          store.payoutPreferences.payoutDetails
                            .stripeConnectAccountId && (
                            <div className="text-xs">
                              <span className="font-medium">
                                Stripe Account ID:
                              </span>{" "}
                              {
                                store.payoutPreferences.payoutDetails
                                  .stripeConnectAccountId
                              }
                            </div>
                          )}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
        </div>

        <div className="bg-white shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Subscription</h2>
            {subscription ? (
              <button
                onClick={() => setShowManageSubscription(true)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Manage
              </button>
            ) : (
              <button
                onClick={() => setShowCreateSubscription(true)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create Subscription
              </button>
            )}
          </div>
          {subscription ? (
            <div className="space-y-6">
              {/* Basic Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Basic Information
                </h3>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">
                      Plan
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900 mt-1">
                      {(subscription.plan as any)?.name || "N/A"}
                    </dd>
                    {(subscription.plan as any)?.description && (
                      <dd className="text-xs text-gray-500 mt-1">
                        {(subscription.plan as any).description}
                      </dd>
                    )}
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">
                      Status
                    </dt>
                    <dd className="text-sm mt-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold ${
                          subscription.status === "active"
                            ? "bg-green-100 text-green-800"
                            : subscription.status === "trialing"
                              ? "bg-blue-100 text-blue-800"
                              : subscription.status === "incomplete" ||
                                  subscription.status === "incomplete_expired"
                                ? "bg-yellow-100 text-yellow-800"
                                : subscription.status === "past_due"
                                  ? "bg-orange-100 text-orange-800"
                                  : subscription.status === "canceled"
                                    ? "bg-gray-100 text-gray-800"
                                    : "bg-red-100 text-red-800"
                        }`}
                      >
                        {subscription.status}
                      </span>
                    </dd>
                    <dd className="text-xs text-gray-500 mt-1">
                      {subscription.status === "active" &&
                        "Subscription is active and working normally"}
                      {subscription.status === "trialing" &&
                        "Subscription is in trial period"}
                      {subscription.status === "incomplete" &&
                        "Payment required to activate subscription"}
                      {subscription.status === "incomplete_expired" &&
                        "Subscription expired - payment was not completed"}
                      {subscription.status === "past_due" &&
                        "Payment failed - store admin needs to update payment method"}
                      {subscription.status === "unpaid" && "Payment is overdue"}
                      {subscription.status === "canceled" &&
                        "Subscription has been canceled"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">
                      Billing Cycle
                    </dt>
                    <dd className="text-sm text-gray-900 mt-1 capitalize">
                      {subscription.billingCycle}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">
                      Renewal Type
                    </dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {subscription.autoRenewal ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-green-600">Automatic</span>
                          <span className="text-xs text-gray-500">
                            (Stripe handles renewal)
                          </span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-yellow-600">Manual</span>
                          <span className="text-xs text-gray-500">
                            (Store admin pays each period)
                          </span>
                        </span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Billing Period Section */}
              <div className="border-b pb-4">
                <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Billing Period
                </h3>
                <dl className="grid grid-cols-2 gap-4">
                  {subscription.currentPeriodStart && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Period Start
                      </dt>
                      <dd className="text-sm text-gray-900 mt-1">
                        {new Date(
                          subscription.currentPeriodStart
                        ).toLocaleDateString()}
                      </dd>
                      <dd className="text-xs text-gray-500 mt-1">
                        {new Date(
                          subscription.currentPeriodStart
                        ).toLocaleTimeString()}
                      </dd>
                    </div>
                  )}
                  {subscription.currentPeriodEnd && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Period End / Renewal Date
                      </dt>
                      <dd className="text-sm text-gray-900 mt-1">
                        {new Date(
                          subscription.currentPeriodEnd
                        ).toLocaleDateString()}
                      </dd>
                      <dd className="text-xs text-gray-500 mt-1">
                        {(() => {
                          const daysLeft = Math.ceil(
                            (new Date(subscription.currentPeriodEnd).getTime() -
                              new Date().getTime()) /
                              (1000 * 60 * 60 * 24)
                          );
                          if (daysLeft > 0) {
                            return `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`;
                          } else {
                            return "Period has ended";
                          }
                        })()}
                      </dd>
                    </div>
                  )}
                  {subscription.cancelAtPeriodEnd && (
                    <div className="col-span-2">
                      <dt className="text-xs font-medium text-yellow-600 uppercase">
                        Cancellation Status
                      </dt>
                      <dd className="text-sm text-yellow-700 font-medium mt-1">
                        Will cancel at period end (
                        {subscription.currentPeriodEnd &&
                          new Date(
                            subscription.currentPeriodEnd
                          ).toLocaleDateString()}
                        )
                      </dd>
                      <dd className="text-xs text-yellow-600 mt-1">
                        Subscription will remain active until the end of the
                        current billing period
                      </dd>
                    </div>
                  )}
                  {subscription.canceledAt && (
                    <div className="col-span-2">
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Canceled At
                      </dt>
                      <dd className="text-sm text-gray-700 mt-1">
                        {new Date(subscription.canceledAt).toLocaleDateString()}{" "}
                        at{" "}
                        {new Date(subscription.canceledAt).toLocaleTimeString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Pricing Section - Right Aligned */}
              <div className="border-b pb-4">
                <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Pricing Breakdown
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">
                      Plan ({subscription.billingCycle})
                    </span>
                    <span className="text-xs font-semibold text-gray-900 text-right">
                      $
                      {subscription.billingCycle === "yearly" &&
                      (subscription.plan as any)?.yearlyPrice
                        ? (subscription.plan as any).yearlyPrice.toFixed(2)
                        : ((subscription.plan as any)?.price || 0).toFixed(2)}
                      /{subscription.billingCycle === "yearly" ? "yr" : "mo"}
                    </span>
                  </div>
                  {store?.businessModel === "plugin-based" &&
                    enabledPlugins &&
                    enabledPlugins.length > 0 && (
                      <>
                        {enabledPlugins.map((enabledPlugin: any) => {
                          const plugin = plugins.find(
                            (p) => p.id === enabledPlugin.id
                          );
                          if (!plugin) return null;
                          const pluginPrice =
                            subscription.billingCycle === "yearly" &&
                            plugin.yearlyPrice
                              ? plugin.yearlyPrice
                              : plugin.price *
                                (subscription.billingCycle === "yearly"
                                  ? 12
                                  : 1);
                          return (
                            <div
                              key={enabledPlugin.id}
                              className="flex justify-between items-center"
                            >
                              <span className="text-xs text-gray-600">
                                {enabledPlugin.name}
                              </span>
                              <span className="text-xs font-semibold text-gray-900 text-right">
                                ${pluginPrice.toFixed(2)}/
                                {subscription.billingCycle === "yearly"
                                  ? "yr"
                                  : "mo"}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-sm font-bold text-indigo-600 text-right">
                        {(() => {
                          const planPrice =
                            subscription.billingCycle === "yearly" &&
                            (subscription.plan as any)?.yearlyPrice
                              ? (subscription.plan as any).yearlyPrice
                              : ((subscription.plan as any)?.price || 0) *
                                (subscription.billingCycle === "yearly"
                                  ? 12
                                  : 1);
                          const pluginsTotal = enabledPlugins.reduce(
                            (total, enabledPlugin) => {
                              const plugin = plugins.find(
                                (p) => p.id === enabledPlugin.id
                              );
                              if (plugin) {
                                const pluginPrice =
                                  subscription.billingCycle === "yearly" &&
                                  plugin.yearlyPrice
                                    ? plugin.yearlyPrice
                                    : plugin.price *
                                      (subscription.billingCycle === "yearly"
                                        ? 12
                                        : 1);
                                return total + pluginPrice;
                              }
                              return total;
                            },
                            0
                          );
                          return `$${(planPrice + pluginsTotal).toFixed(2)}/${subscription.billingCycle === "yearly" ? "yr" : "mo"}`;
                        })()}
                      </span>
                    </div>
                    {subscription.currentPeriodEnd && (
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        Renewal:{" "}
                        {new Date(
                          subscription.currentPeriodEnd
                        ).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Additional Information
                </h3>
                <dl className="space-y-3">
                  {subscription.stripeSubscriptionId &&
                    !subscription.stripeSubscriptionId.startsWith(
                      "manual_"
                    ) && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">
                          Stripe Subscription ID
                        </dt>
                        <dd className="text-xs text-gray-600 font-mono break-all mt-1">
                          {subscription.stripeSubscriptionId}
                        </dd>
                      </div>
                    )}
                  {subscription.stripeSubscriptionId &&
                    subscription.stripeSubscriptionId.startsWith("manual_") && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">
                          Subscription Type
                        </dt>
                        <dd className="text-sm text-gray-700 mt-1">
                          Manual Renewal (No Stripe subscription - one-time
                          payments)
                        </dd>
                      </div>
                    )}
                  {(subscription.status === "incomplete" ||
                    subscription.status === "past_due" ||
                    subscription.status === "unpaid" ||
                    subscription.status === "incomplete_expired") && (
                    <div>
                      <dt className="text-xs font-medium text-red-500 uppercase">
                        Payment Required
                      </dt>
                      <dd className="text-sm text-red-700 font-medium mt-1">
                        {subscription.status === "incomplete_expired"
                          ? "Subscription has expired due to incomplete payment. Store admin needs to create a new subscription."
                          : subscription.status === "incomplete"
                            ? "Payment required to activate. Store admin needs to complete payment in their portal."
                            : subscription.status === "past_due"
                              ? "Payment failed. Store admin needs to update payment method in their portal."
                              : "Payment is overdue. Store admin needs to complete payment in their portal."}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Scheduled Changes Alert */}
              {subscription.hasScheduledChanges &&
                (() => {
                  // Calculate scheduled total fee
                  const calculateScheduledTotal = () => {
                    let total = 0;
                    const billingCycle = subscription.billingCycle || "monthly";

                    // Add scheduled plan price (or current plan if no scheduled change)
                    const planToUse =
                      subscription.scheduledPlanId || subscription.plan;
                    if (planToUse) {
                      const plan = planToUse as any;
                      if (billingCycle === "yearly" && plan.yearlyPrice) {
                        total += plan.yearlyPrice;
                      } else {
                        total +=
                          (plan.price || 0) *
                          (billingCycle === "yearly" ? 12 : 1);
                      }
                    }

                    // Add scheduled plugins' prices
                    // If scheduledPluginIds is provided, use those; otherwise keep current plugins
                    const pluginsToUse =
                      subscription.scheduledPluginIds !== undefined
                        ? subscription.scheduledPluginIds
                        : enabledPlugins.map((ep: any) => ep.id);

                    if (pluginsToUse && pluginsToUse.length > 0) {
                      pluginsToUse.forEach((pluginId: string) => {
                        const plugin = plugins.find((p) => p.id === pluginId);
                        if (plugin) {
                          if (billingCycle === "yearly" && plugin.yearlyPrice) {
                            total += plugin.yearlyPrice;
                          } else {
                            total +=
                              plugin.price *
                              (billingCycle === "yearly" ? 12 : 1);
                          }
                        }
                      });
                    }

                    return total;
                  };

                  const scheduledTotal = calculateScheduledTotal();
                  const currentTotal = (() => {
                    const planPrice =
                      subscription.billingCycle === "yearly" &&
                      (subscription.plan as any)?.yearlyPrice
                        ? (subscription.plan as any).yearlyPrice
                        : ((subscription.plan as any)?.price || 0) *
                          (subscription.billingCycle === "yearly" ? 12 : 1);
                    const pluginsTotal = enabledPlugins.reduce(
                      (total, enabledPlugin) => {
                        const plugin = plugins.find(
                          (p) => p.id === enabledPlugin.id
                        );
                        if (plugin) {
                          const pluginPrice =
                            subscription.billingCycle === "yearly" &&
                            plugin.yearlyPrice
                              ? plugin.yearlyPrice
                              : plugin.price *
                                (subscription.billingCycle === "yearly"
                                  ? 12
                                  : 1);
                          return total + pluginPrice;
                        }
                        return total;
                      },
                      0
                    );
                    return planPrice + pluginsTotal;
                  })();

                  return (
                    <div className="bg-blue-50 border border-blue-200 p-4">
                      <div className="flex items-start gap-2">
                        <svg
                          className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-blue-900 mb-1">
                            Scheduled Changes
                          </h4>
                          <p className="text-xs text-blue-700 mb-2">
                            The following changes are scheduled to take effect
                            at the end of the current subscription period:
                          </p>
                          <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside mb-3">
                            {subscription.scheduledPlanId && (
                              <li>
                                Plan will change to:{" "}
                                <span className="font-semibold">
                                  {(subscription.scheduledPlanId as any)
                                    ?.name || "New Plan"}
                                </span>
                              </li>
                            )}
                            {subscription.scheduledPluginIds &&
                              subscription.scheduledPluginIds.length > 0 && (
                                <li>
                                  Plugin changes scheduled (
                                  {subscription.scheduledPluginIds.length}{" "}
                                  plugin
                                  {subscription.scheduledPluginIds.length !== 1
                                    ? "s"
                                    : ""}
                                  )
                                </li>
                              )}
                            {subscription.currentPeriodEnd && (
                              <li className="font-medium mt-2">
                                Effective Date:{" "}
                                {new Date(
                                  subscription.currentPeriodEnd
                                ).toLocaleDateString()}
                              </li>
                            )}
                          </ul>
                          <div className="bg-white border border-blue-300 p-3 mt-2">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-xs font-medium text-blue-900">
                                  New Total Fee
                                </div>
                                <div className="text-xs text-blue-700">
                                  {subscription.currentPeriodEnd && (
                                    <>
                                      Starting{" "}
                                      {new Date(
                                        subscription.currentPeriodEnd
                                      ).toLocaleDateString()}
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-blue-900">
                                  ${scheduledTotal.toFixed(2)}/
                                  {subscription.billingCycle === "yearly"
                                    ? "yr"
                                    : "mo"}
                                </div>
                                {currentTotal !== scheduledTotal && (
                                  <div className="text-xs text-blue-600 mt-1">
                                    {currentTotal < scheduledTotal ? (
                                      <span className="text-red-600">
                                        ↑ $
                                        {(
                                          scheduledTotal - currentTotal
                                        ).toFixed(2)}{" "}
                                        increase
                                      </span>
                                    ) : (
                                      <span className="text-green-600">
                                        ↓ $
                                        {(
                                          currentTotal - scheduledTotal
                                        ).toFixed(2)}{" "}
                                        decrease
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  "Are you sure you want to cancel the scheduled changes?"
                                )
                              ) {
                                return;
                              }
                              try {
                                setSubmitting(true);
                                setError(null);
                                await platformApiService.cancelScheduledChanges(
                                  storeId!
                                );
                                alert(
                                  "Scheduled changes cancelled successfully"
                                );
                                fetchStoreDetails();
                              } catch (err: any) {
                                setError(
                                  err.response?.data?.error ||
                                    "Failed to cancel scheduled changes"
                                );
                              } finally {
                                setSubmitting(false);
                              }
                            }}
                            disabled={submitting}
                            className="mt-3 w-full px-3 py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {submitting
                              ? "Cancelling..."
                              : "Cancel Scheduled Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {/* Testing Actions for Active/Trialing Subscriptions (Non-Production Only) */}
              {process.env.NODE_ENV !== "production" &&
                (subscription.status === "active" ||
                  subscription.status === "trialing") && (
                  <div className="border-t pt-4 space-y-3">
                    <div className="bg-red-50 border border-red-200 p-3">
                      <p className="text-sm text-red-800 font-medium mb-2">
                        ⚠️ Testing Actions (Not Available in Production)
                      </p>
                      <p className="text-xs text-red-700 mb-3">
                        These actions are for testing purposes only and will not
                        be available in production environments.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleManualExpiration}
                          disabled={expiring}
                          className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          title="Expire subscription immediately (testing only)"
                        >
                          {expiring ? "Expiring..." : "Expire Subscription"}
                        </button>
                      </div>
                      <p className="text-xs text-red-600 mt-2 italic">
                        Warning: This will immediately expire the subscription
                        and set status to incomplete_expired.
                      </p>
                    </div>
                  </div>
                )}

              {/* Manual Activation for Incomplete Subscriptions */}
              {(subscription.status === "incomplete" ||
                subscription.status === "incomplete_expired" ||
                subscription.status === "past_due" ||
                subscription.status === "unpaid") && (
                <div className="border-t pt-4 space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 p-3">
                    <p className="text-sm text-yellow-800 font-medium mb-2">
                      Payment Required
                    </p>
                    <p className="text-xs text-yellow-700 mb-3">
                      This subscription requires payment to activate. Store
                      admin needs to complete payment in their portal. You can
                      manually activate it here if payment has been received
                      outside the system.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleManualActivation}
                        disabled={activating}
                        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {activating ? "Activating..." : "Activate Manually"}
                      </button>
                      {/* Only show expire button in non-production environments */}
                      {process.env.NODE_ENV !== "production" &&
                        subscription.status !== "incomplete_expired" &&
                        subscription.status !== "unpaid" && (
                          <button
                            onClick={handleManualExpiration}
                            disabled={expiring}
                            className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            title="Testing only - Not available in production"
                          >
                            {expiring ? "Expiring..." : "Expire Subscription (Test)"}
                          </button>
                        )}
                    </div>
                    <p className="text-xs text-yellow-600 mt-2 italic">
                      Note: Store admin can complete payment in their
                      subscription portal. Manual activation should only be used
                      if payment was received outside the system.
                    </p>
                    {process.env.NODE_ENV !== "production" && (
                      <p className="text-xs text-red-600 mt-1 italic">
                        ⚠️ Expire button is for testing only and not available in production.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Plan Details */}
              {subscription.plan && (
                <div className="border-t pt-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Plan Details
                    </h4>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">
                        {subscription.billingCycle === "yearly" &&
                        (subscription.plan as any)?.yearlyPrice
                          ? `$${(subscription.plan as any).yearlyPrice}/year`
                          : `$${(subscription.plan as any)?.price || 0}/month`}
                      </div>
                    </div>
                  </div>
                  {(subscription.plan as any).description && (
                    <p className="text-xs text-gray-600 mb-2">
                      {(subscription.plan as any).description}
                    </p>
                  )}
                  {(subscription.plan as any).features &&
                    (subscription.plan as any).features.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">
                          Features:
                        </p>
                        <ul className="text-xs text-gray-600 space-y-0.5">
                          {(subscription.plan as any).features
                            .slice(0, 3)
                            .map((feature: string, index: number) => (
                              <li key={index} className="flex items-start">
                                <span className="mr-1">•</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          {(subscription.plan as any).features.length > 3 && (
                            <li className="text-gray-500 italic">
                              +{(subscription.plan as any).features.length - 3}{" "}
                              more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              )}

              {/* Enabled Plugins - Only for plugin-based stores */}
              {store?.businessModel === "plugin-based" && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Enabled Plugins ({enabledPlugins?.length || 0})
                  </h4>
                  {enabledPlugins && enabledPlugins.length > 0 ? (
                    <div className="flex gap-4 mb-3 relative">
                      {/* Free Plugins - Left Column */}
                      <div className="flex-1">
                        {(() => {
                          const freePlugins = enabledPlugins.filter(
                            (enabledPlugin: any) => {
                              const plugin = plugins.find(
                                (p) => p.id === enabledPlugin.id
                              );
                              if (!plugin) return false;
                              const pluginPrice =
                                subscription.billingCycle === "yearly" &&
                                plugin.yearlyPrice
                                  ? plugin.yearlyPrice
                                  : plugin.price *
                                    (subscription.billingCycle === "yearly"
                                      ? 12
                                      : 1);
                              return pluginPrice === 0;
                            }
                          );
                          return freePlugins.length > 0 ? (
                            <div className="space-y-2">
                              {freePlugins.map((enabledPlugin: any) => (
                                <div
                                  key={enabledPlugin.id}
                                  className="p-2 bg-gray-50 rounded border border-gray-200"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-medium text-gray-900">
                                      {enabledPlugin.name}
                                    </span>
                                    <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                                      Active
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">
                              No free plugins
                            </div>
                          );
                        })()}
                      </div>

                      {/* Vertical Separator */}
                      <div className="w-px bg-gray-300"></div>

                      {/* Paid Plugins - Right Column */}
                      <div className="flex-1">
                        {(() => {
                          const paidPlugins = enabledPlugins.filter(
                            (enabledPlugin: any) => {
                              const plugin = plugins.find(
                                (p) => p.id === enabledPlugin.id
                              );
                              if (!plugin) return false;
                              const pluginPrice =
                                subscription.billingCycle === "yearly" &&
                                plugin.yearlyPrice
                                  ? plugin.yearlyPrice
                                  : plugin.price *
                                    (subscription.billingCycle === "yearly"
                                      ? 12
                                      : 1);
                              return pluginPrice > 0;
                            }
                          );
                          return paidPlugins.length > 0 ? (
                            <div className="space-y-2">
                              {paidPlugins.map((enabledPlugin: any) => {
                                const plugin = plugins.find(
                                  (p) => p.id === enabledPlugin.id
                                );
                                const pluginPrice = plugin
                                  ? subscription.billingCycle === "yearly" &&
                                    plugin.yearlyPrice
                                    ? plugin.yearlyPrice
                                    : plugin.price *
                                      (subscription.billingCycle === "yearly"
                                        ? 12
                                        : 1)
                                  : 0;
                                return (
                                  <div
                                    key={enabledPlugin.id}
                                    className="p-2 bg-gray-50 rounded border border-gray-200"
                                  >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <div className="flex items-center gap-2 flex-1">
                                        <span className="text-xs font-medium text-gray-900">
                                          {enabledPlugin.name}
                                        </span>
                                        <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                                          Active
                                        </span>
                                      </div>
                                      {plugin && (
                                        <div className="text-right">
                                          <div className="text-xs font-semibold text-gray-900">
                                            ${pluginPrice.toFixed(2)}/
                                            {subscription.billingCycle ===
                                            "yearly"
                                              ? "yr"
                                              : "mo"}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {plugin && (
                                      <div className="text-xs text-gray-500">
                                        Billing:{" "}
                                        <span className="font-medium capitalize">
                                          {enabledPlugin.billingCycle}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">
                              No paid plugins
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">
                      No plugins enabled for this subscription
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 p-4">
              <p className="text-sm text-gray-500">No active subscription</p>
              <p className="text-xs text-gray-400 mt-1">
                Create a subscription to enable store features
              </p>
            </div>
          )}
        </div>
      </div>

      {usage && (
        <div className="bg-white shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t("platform.storeDetail.usageAndLimits") || "Usage & Limits"}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.products") || "Products"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.products || 0} / {usage.limits?.products ?? (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
              <Link
                to={`/platform/stores/${storeId}/products`}
                className="text-xs text-indigo-600 hover:text-indigo-800 underline mt-1 inline-block"
              >
                {t("platform.storeDetail.manageProducts") || "Manage Products"} →
              </Link>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.orders") || "Orders"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.orders || 0} / {usage.limits?.orders ?? (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.pages") || "Pages"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.pages || 0} / {usage.limits?.pages ?? (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.categories") || "Categories"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.categories || 0} /{" "}
                {usage.limits?.categories ?? (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.users") || "Users"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.users || 0} / {usage.limits?.users ?? (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t("platform.storeDetail.mediaSpace") || "Media Space"}</dt>
              <dd className="text-2xl font-bold text-gray-900">
                {usage.usage?.mediaSpaceInMB 
                  ? `${usage.usage.mediaSpaceInMB.toFixed(2)} ${t("common.mb")}`
                  : usage.usage?.mediaSpace 
                  ? `${(usage.usage.mediaSpace / (1024 * 1024)).toFixed(2)} ${t("common.mb")}`
                  : `0 ${t("common.mb")}`} /{" "}
                {usage.limits?.mediaSpace 
                  ? `${(usage.limits.mediaSpace / (1024 * 1024)).toFixed(2)} ${t("common.mb")}`
                  : usage.mediaSpaceLimitInMB
                  ? `${usage.mediaSpaceLimitInMB.toFixed(2)} ${t("common.mb")}`
                  : (t("platform.storeDetail.unlimited") || "∞")}
              </dd>
              {usage.mediaSpaceUsagePercentage !== undefined && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        usage.mediaSpaceUsagePercentage >= 90
                          ? "bg-red-500"
                          : usage.mediaSpaceUsagePercentage >= 75
                          ? "bg-yellow-500"
                          : "bg-green-500"
                      }`}
                      style={{ width: `${Math.min(usage.mediaSpaceUsagePercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {usage.mediaSpaceUsagePercentage}% {t("platform.storeDetail.used") || "used"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Subscription Modal */}
      {showCreateSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white  max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold">Create Subscription</h3>
              <button
                onClick={() => {
                  setShowCreateSubscription(false);
                  setError(null);
                  setSelectedPlan("");
                  setSelectedPlugins([]);
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Fixed Total Section */}
            {selectedPlan && (
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex-shrink-0">
                <div className="bg-white p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium text-gray-900">
                      {(() => {
                        const plan = plans.find((p) => p._id === selectedPlan);
                        if (!plan) return "$0";
                        if (billingCycle === "yearly" && plan.yearlyPrice) {
                          return `$${plan.yearlyPrice.toFixed(2)}/year`;
                        }
                        return `$${plan.price.toFixed(2)}/${billingCycle === "yearly" ? "year" : "month"}`;
                      })()}
                    </span>
                  </div>

                  {store?.businessModel === "plugin-based" &&
                    selectedPlugins.length > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Plugins ({selectedPlugins.length}):
                          </span>
                          <span className="font-medium text-gray-900">
                            {(() => {
                              let pluginsTotal = 0;
                              selectedPlugins.forEach((pluginId) => {
                                const plugin = plugins.find(
                                  (p) => p.id === pluginId
                                );
                                if (plugin) {
                                  if (
                                    billingCycle === "yearly" &&
                                    plugin.yearlyPrice
                                  ) {
                                    pluginsTotal += plugin.yearlyPrice;
                                  } else {
                                    pluginsTotal +=
                                      plugin.price *
                                      (billingCycle === "yearly" ? 12 : 1);
                                  }
                                }
                              });
                              return `$${pluginsTotal.toFixed(2)}/${billingCycle === "yearly" ? "year" : "month"}`;
                            })()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 pl-2">
                          {selectedPlugins.map((pluginId) => {
                            const plugin = plugins.find(
                              (p) => p.id === pluginId
                            );
                            return plugin ? (
                              <div
                                key={pluginId}
                                className="flex justify-between"
                              >
                                <span>{plugin.name}:</span>
                                <span>
                                  $
                                  {billingCycle === "yearly" &&
                                  plugin.yearlyPrice
                                    ? plugin.yearlyPrice.toFixed(2)
                                    : (
                                        plugin.price *
                                        (billingCycle === "yearly" ? 12 : 1)
                                      ).toFixed(2)}
                                  /{billingCycle === "yearly" ? "yr" : "mo"}
                                </span>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </>
                    )}

                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-indigo-600">
                      ${calculateTotal().toFixed(2)}/
                      {billingCycle === "yearly" ? "year" : "month"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              <form
                id="create-subscription-form"
                onSubmit={handleCreateSubscription}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Plan <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="">Choose a plan...</option>
                      {plans
                        .filter((plan) => plan.isActive)
                        .map((plan) => (
                          <option key={plan._id} value={plan._id}>
                            {plan.name} - ${plan.price}/month
                            {plan.yearlyPrice && ` ($${plan.yearlyPrice}/year)`}
                          </option>
                        ))}
                    </select>
                  </div>

                  {selectedPlan && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Billing Cycle <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={billingCycle}
                        onChange={(e) =>
                          setBillingCycle(
                            e.target.value as "monthly" | "yearly"
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  )}

                  {/* Plugin Selection for Plugin-Based Stores */}
                  {store?.businessModel === "plugin-based" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Plugins/Features{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Select the plugins/features to enable for this store
                      </p>
                      <div className="max-h-60 overflow-y-auto border border-gray-300 p-3 space-y-2">
                        {plugins
                          .filter((plugin) => plugin.isActive)
                          .map((plugin) => {
                            const pluginId = plugin.id; // Use the plugin ID (not _id)
                            return (
                              <label
                                key={plugin._id || plugin.id}
                                className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPlugins.includes(pluginId)}
                                  onChange={() => handlePluginToggle(pluginId)}
                                  className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm text-gray-900">
                                    {plugin.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {plugin.description}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {billingCycle === "yearly" &&
                                    plugin.yearlyPrice ? (
                                      <span>${plugin.yearlyPrice}/year</span>
                                    ) : (
                                      <span>${plugin.price}/month</span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        {plugins.filter((plugin) => plugin.isActive).length ===
                          0 && (
                          <p className="text-sm text-gray-500 text-center py-4">
                            No active plugins available
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Fixed Footer */}
            <div className="border-t border-gray-200 p-6 bg-white flex-shrink-0">
              <div className="flex gap-2">
                <button
                  type="submit"
                  form="create-subscription-form"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? "Creating..." : "Create Subscription"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSubscription(false);
                    setError(null);
                    setSelectedPlan("");
                    setSelectedPlugins([]);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Subscription Modal */}
      {showManageSubscription && subscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white  max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold">Manage Subscription</h3>
              <button
                onClick={() => setShowManageSubscription(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Fixed Total Section - Show when plan is selected */}
            {(selectedPlan ||
              (subscription?.plan && !subscription.status) ||
              subscription.status === "canceled") && (
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex-shrink-0">
                <div className="bg-white p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium text-gray-900">
                      {(() => {
                        const planId =
                          selectedPlan ||
                          (subscription.plan as any)?._id ||
                          subscription.planId;
                        const plan = plans.find((p) => p._id === planId);
                        if (!plan) return "$0";
                        const currentBillingCycle =
                          subscription?.billingCycle || billingCycle;
                        if (
                          currentBillingCycle === "yearly" &&
                          plan.yearlyPrice
                        ) {
                          return `$${plan.yearlyPrice.toFixed(2)}/year`;
                        }
                        return `$${plan.price.toFixed(2)}/${currentBillingCycle === "yearly" ? "year" : "month"}`;
                      })()}
                    </span>
                  </div>

                  {store?.businessModel === "plugin-based" &&
                    selectedPlugins.length > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Plugins ({selectedPlugins.length}):
                          </span>
                          <span className="font-medium text-gray-900">
                            {(() => {
                              let pluginsTotal = 0;
                              const currentBillingCycle =
                                subscription?.billingCycle || billingCycle;
                              selectedPlugins.forEach((pluginId) => {
                                const plugin = plugins.find(
                                  (p) => p.id === pluginId
                                );
                                if (plugin) {
                                  if (
                                    currentBillingCycle === "yearly" &&
                                    plugin.yearlyPrice
                                  ) {
                                    pluginsTotal += plugin.yearlyPrice;
                                  } else {
                                    pluginsTotal +=
                                      plugin.price *
                                      (currentBillingCycle === "yearly"
                                        ? 12
                                        : 1);
                                  }
                                }
                              });
                              return `$${pluginsTotal.toFixed(2)}/${currentBillingCycle === "yearly" ? "year" : "month"}`;
                            })()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 pl-2 space-y-1">
                          {selectedPlugins.map((pluginId) => {
                            const plugin = plugins.find(
                              (p) => p.id === pluginId
                            );
                            const currentBillingCycle =
                              subscription?.billingCycle || billingCycle;
                            return plugin ? (
                              <div
                                key={pluginId}
                                className="flex justify-between"
                              >
                                <span>{plugin.name}:</span>
                                <span>
                                  $
                                  {currentBillingCycle === "yearly" &&
                                  plugin.yearlyPrice
                                    ? plugin.yearlyPrice.toFixed(2)
                                    : plugin.price.toFixed(2)}
                                  /
                                  {currentBillingCycle === "yearly"
                                    ? "yr"
                                    : "mo"}
                                </span>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </>
                    )}

                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-base font-semibold">
                      <span className="text-gray-900">Total:</span>
                      <span className="text-indigo-600">
                        {(() => {
                          const currentBillingCycle =
                            subscription?.billingCycle || billingCycle;
                          const planId =
                            selectedPlan ||
                            (subscription.plan as any)?._id ||
                            subscription.planId;
                          let total = 0;

                          // Add plan price
                          if (planId) {
                            const plan = plans.find((p) => p._id === planId);
                            if (plan) {
                              if (
                                currentBillingCycle === "yearly" &&
                                plan.yearlyPrice
                              ) {
                                total += plan.yearlyPrice;
                              } else {
                                total +=
                                  plan.price *
                                  (currentBillingCycle === "yearly" ? 12 : 1);
                              }
                            }
                          }

                          // Add selected plugins' prices
                          if (
                            store?.businessModel === "plugin-based" &&
                            selectedPlugins.length > 0
                          ) {
                            selectedPlugins.forEach((pluginId) => {
                              const plugin = plugins.find(
                                (p) => p.id === pluginId
                              );
                              if (plugin) {
                                if (
                                  currentBillingCycle === "yearly" &&
                                  plugin.yearlyPrice
                                ) {
                                  total += plugin.yearlyPrice;
                                } else {
                                  total +=
                                    plugin.price *
                                    (currentBillingCycle === "yearly" ? 12 : 1);
                                }
                              }
                            });
                          }

                          return `$${total.toFixed(2)}/${currentBillingCycle === "yearly" ? "year" : "month"}`;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              <div className="space-y-6">
                {/* Current Subscription Details */}
                <div className="bg-gray-50 p-4 space-y-3">
                  <h4 className="font-semibold text-gray-900">
                    Current Subscription
                  </h4>
                  <dl className="grid grid-cols-2 gap-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Plan Name
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {subscription.plan?.name || "N/A"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Status
                      </dt>
                      <dd className="text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold ${
                            subscription.status === "active"
                              ? "bg-green-100 text-green-800"
                              : subscription.status === "trialing"
                                ? "bg-blue-100 text-blue-800"
                                : subscription.status === "past_due"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subscription.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Billing Cycle
                      </dt>
                      <dd className="text-sm text-gray-900 capitalize">
                        {subscription.billingCycle || "N/A"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Price
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {subscription.billingCycle === "yearly" &&
                        subscription.plan?.yearlyPrice
                          ? `$${subscription.plan.yearlyPrice}/year`
                          : subscription.plan?.price
                            ? `$${subscription.plan.price}/month`
                            : "N/A"}
                      </dd>
                    </div>
                    {subscription.currentPeriodStart &&
                      subscription.currentPeriodEnd && (
                        <>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Period Start
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(
                                subscription.currentPeriodStart
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Period End
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(
                                subscription.currentPeriodEnd
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                        </>
                      )}
                    {subscription.cancelAtPeriodEnd && (
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500">
                          Cancellation
                        </dt>
                        <dd className="text-sm text-yellow-700 font-medium">
                          Will cancel at period end
                        </dd>
                      </div>
                    )}
                    {subscription.hasScheduledChanges &&
                      (() => {
                        // Calculate scheduled total fee
                        const calculateScheduledTotal = () => {
                          let total = 0;
                          const billingCycle =
                            subscription.billingCycle || "monthly";

                          // Add scheduled plan price (or current plan if no scheduled change)
                          const planToUse =
                            subscription.scheduledPlanId || subscription.plan;
                          if (planToUse) {
                            const plan = planToUse as any;
                            if (billingCycle === "yearly" && plan.yearlyPrice) {
                              total += plan.yearlyPrice;
                            } else {
                              total +=
                                (plan.price || 0) *
                                (billingCycle === "yearly" ? 12 : 1);
                            }
                          }

                          // Add scheduled plugins' prices
                          // If scheduledPluginIds is provided, use those; otherwise keep current plugins
                          const pluginsToUse =
                            subscription.scheduledPluginIds !== undefined
                              ? subscription.scheduledPluginIds
                              : enabledPlugins.map((ep: any) => ep.id);

                          if (pluginsToUse && pluginsToUse.length > 0) {
                            pluginsToUse.forEach((pluginId: string) => {
                              const plugin = plugins.find(
                                (p) => p.id === pluginId
                              );
                              if (plugin) {
                                if (
                                  billingCycle === "yearly" &&
                                  plugin.yearlyPrice
                                ) {
                                  total += plugin.yearlyPrice;
                                } else {
                                  total +=
                                    plugin.price *
                                    (billingCycle === "yearly" ? 12 : 1);
                                }
                              }
                            });
                          }

                          return total;
                        };

                        const scheduledTotal = calculateScheduledTotal();

                        return (
                          <div className="col-span-2">
                            <dt className="text-sm font-medium text-gray-500">
                              Scheduled Changes
                            </dt>
                            <dd className="text-sm text-blue-700 font-medium">
                              <div className="space-y-2">
                                <div>
                                  Changes scheduled for end of current period
                                </div>
                                {subscription.scheduledPlanId && (
                                  <div className="text-xs">
                                    • Plan change scheduled
                                  </div>
                                )}
                                {subscription.scheduledPluginIds &&
                                  subscription.scheduledPluginIds.length >
                                    0 && (
                                    <div className="text-xs">
                                      • Plugin changes scheduled
                                    </div>
                                  )}
                                {subscription.currentPeriodEnd && (
                                  <div className="text-xs text-gray-600">
                                    Effective:{" "}
                                    {new Date(
                                      subscription.currentPeriodEnd
                                    ).toLocaleDateString()}
                                  </div>
                                )}
                                <div className="mt-2 pt-2 border-t border-blue-200">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs font-medium text-blue-900">
                                      New Total Fee:
                                    </span>
                                    <span className="text-sm font-bold text-blue-900">
                                      ${scheduledTotal.toFixed(2)}/
                                      {subscription.billingCycle === "yearly"
                                        ? "yr"
                                        : "mo"}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (
                                      !window.confirm(
                                        "Are you sure you want to cancel the scheduled changes?"
                                      )
                                    ) {
                                      return;
                                    }
                                    try {
                                      setSubmitting(true);
                                      setError(null);
                                      await platformApiService.cancelScheduledChanges(
                                        storeId!
                                      );
                                      alert(
                                        "Scheduled changes cancelled successfully"
                                      );
                                      setShowManageSubscription(false);
                                      fetchStoreDetails();
                                    } catch (err: any) {
                                      setError(
                                        err.response?.data?.error ||
                                          "Failed to cancel scheduled changes"
                                      );
                                    } finally {
                                      setSubmitting(false);
                                    }
                                  }}
                                  disabled={submitting}
                                  className="mt-2 w-full px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  {submitting
                                    ? "Cancelling..."
                                    : "Cancel Scheduled Changes"}
                                </button>
                              </div>
                            </dd>
                          </div>
                        );
                      })()}
                  </dl>
                </div>

                {/* Plan Description and Features */}
                {subscription.plan && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Plan Details
                    </h4>
                    {subscription.plan.description && (
                      <p className="text-sm text-gray-600 mb-3">
                        {subscription.plan.description}
                      </p>
                    )}
                    {subscription.plan.features &&
                      subscription.plan.features.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Features:
                          </h5>
                          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                            {subscription.plan.features.map(
                              (feature: string, index: number) => (
                                <li key={index}>{feature}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    {subscription.plan.limits && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">
                          {t("platform.storeDetail.limits") || "Limits:"}
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          {subscription.plan.limits.products !== undefined && (
                            <div>
                              <span className="text-gray-500">{t("platform.storeDetail.products") || "Products"}: </span>
                              <span className="font-medium">
                                {subscription.plan.limits.products ?? (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                          {subscription.plan.limits.orders !== undefined && (
                            <div>
                              <span className="text-gray-500">{t("platform.storeDetail.orders") || "Orders"}: </span>
                              <span className="font-medium">
                                {subscription.plan.limits.orders ?? (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                          {subscription.plan.limits.pages !== undefined && (
                            <div>
                              <span className="text-gray-500">{t("platform.storeDetail.pages") || "Pages"}: </span>
                              <span className="font-medium">
                                {subscription.plan.limits.pages ?? (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                          {subscription.plan.limits.categories !==
                            undefined && (
                            <div>
                              <span className="text-gray-500">
                                {t("platform.storeDetail.categories") || "Categories"}:{" "}
                              </span>
                              <span className="font-medium">
                                {subscription.plan.limits.categories ?? (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                          {subscription.plan.limits.users !== undefined && (
                            <div>
                              <span className="text-gray-500">{t("platform.storeDetail.users") || "Users"}: </span>
                              <span className="font-medium">
                                {subscription.plan.limits.users ?? (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                          {subscription.plan.limits.mediaSpace !== undefined && (
                            <div>
                              <span className="text-gray-500">{t("platform.storeDetail.mediaSpace") || "Media Space"}: </span>
                              <span className="font-medium">
                                {subscription.plan.limits.mediaSpace 
                                  ? `${(subscription.plan.limits.mediaSpace / (1024 * 1024)).toFixed(2)} ${t("common.mb")}`
                                  : (t("platform.storeDetail.unlimited") || "∞")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Change Plan Section */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Plan
                  </label>
                  <select
                    defaultValue={
                      (subscription.plan as any)?._id || subscription.planId
                    }
                    onChange={(e) => {
                      // Don't auto-submit, let user click update button
                      setSelectedPlan(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={submitting}
                  >
                    {plans
                      .filter((plan) => plan.isActive)
                      .map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.name} - ${plan.price}/month
                          {plan.yearlyPrice && ` ($${plan.yearlyPrice}/year)`}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Plugin Selection for Plugin-Based Stores */}
                {store?.businessModel === "plugin-based" && (
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Plugins/Features
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select the plugins/features to enable for this store
                    </p>
                    <div className="max-h-60 overflow-y-auto border border-gray-300 p-3 space-y-2">
                      {plugins
                        .filter((plugin) => plugin.isActive)
                        .map((plugin) => {
                          const pluginId = plugin.id; // Use the plugin ID (not _id)
                          const isEnabled = enabledPlugins.some(
                            (ep: any) => ep.id === pluginId
                          );
                          return (
                            <label
                              key={plugin._id || plugin.id}
                              className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPlugins.includes(pluginId)}
                                onChange={() => handlePluginToggle(pluginId)}
                                className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-sm text-gray-900">
                                    {plugin.name}
                                  </div>
                                  {isEnabled && (
                                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                                      Enabled
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {plugin.description}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {subscription.billingCycle === "yearly" &&
                                  plugin.yearlyPrice ? (
                                    <span>${plugin.yearlyPrice}/year</span>
                                  ) : (
                                    <span>${plugin.price}/month</span>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      {plugins.filter((plugin) => plugin.isActive).length ===
                        0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No active plugins available
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Platform Admin Options */}
                <div className="border-t pt-4 space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 p-3">
                    <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                      Platform Admin Options
                    </h4>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyImmediate}
                        onChange={(e) => setApplyImmediate(e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-yellow-900">
                          Apply changes immediately
                        </div>
                        <div className="text-xs text-yellow-700 mt-1">
                          If unchecked, changes will be scheduled for the end of
                          the current subscription cycle
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="border-t border-gray-200 p-6 bg-white flex-shrink-0 space-y-3">
                {/* Update Button */}
                <button
                  onClick={() => {
                    const planId =
                      selectedPlan ||
                      (subscription.plan as any)?._id ||
                      subscription.planId;
                    if (planId) {
                      handleUpdateSubscription(planId);
                    }
                  }}
                  disabled={
                    submitting ||
                    subscription?.status === "incomplete" ||
                    subscription?.status === "incomplete_expired"
                  }
                  className="w-full px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    subscription?.status === "incomplete" ||
                    subscription?.status === "incomplete_expired"
                      ? "Cannot update incomplete subscription. Please complete payment first."
                      : ""
                  }
                >
                  {submitting
                    ? "Updating..."
                    : applyImmediate
                      ? "Update Subscription (Immediate)"
                      : "Schedule Update for End of Cycle"}
                </button>
                {(subscription?.status === "incomplete" ||
                  subscription?.status === "incomplete_expired") && (
                  <p className="text-xs text-red-600 mt-1">
                    Cannot update incomplete subscription. Please complete
                    payment first.
                  </p>
                )}

                {/* Cancel Subscription Section */}
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 p-3">
                    <h4 className="text-sm font-semibold text-red-900 mb-2">
                      Cancel Subscription
                    </h4>
                    <label className="flex items-start gap-2 cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={applyImmediate}
                        onChange={(e) => {
                          setApplyImmediate(e.target.checked);
                          if (!e.target.checked) {
                            setProcessRefund(false); // Can't refund if not immediate
                          }
                        }}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-900">
                          Cancel immediately
                        </div>
                        <div className="text-xs text-red-700 mt-1">
                          If unchecked, cancellation will be scheduled for the
                          end of the current period
                        </div>
                      </div>
                    </label>
                    {applyImmediate && (
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={processRefund}
                          onChange={(e) => setProcessRefund(e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-red-900">
                            Process time-based refund
                          </div>
                          <div className="text-xs text-red-700 mt-1">
                            Calculate and process a refund for the remaining
                            subscription period
                          </div>
                        </div>
                      </label>
                    )}
                  </div>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={submitting}
                    className="w-full px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {submitting
                      ? "Processing..."
                      : applyImmediate
                        ? "Cancel Immediately"
                        : "Schedule Cancellation"}
                  </button>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowManageSubscription(false);
                    setError(null);
                    setSelectedPlugins([]);
                    setSelectedPlan("");
                    setApplyImmediate(false);
                    setProcessRefund(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformStoreDetail;
